import { ErrorMessage } from "@hookform/error-message";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../components/Component";
import Table from "../../components/react-table/Table";
import { DELETE, GET, POST, PUT } from "../../helper/api";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { INGREDIENT_CATEGORY_NAME } from "../../utils/Utils";

const ROUTE = "/sale-commission-setting";

const BONUS_KEYS = ["BHD", "DTP", "VNG", "VNB"];

const DEFAULT_FORM = {
  maxWeight: "",
  bonus: Object.fromEntries(BONUS_KEYS.map((k) => [k, ""])),
};

const SaleCommissionSettingManager = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [view, setView] = useState({ add: false, edit: false, isDelete: false });

  const {
    register,
    handleSubmit,
    reset,
    control,
    errors,
  } = useForm({ defaultValues: DEFAULT_FORM });

  const fetchData = () => {
    setLoading(true);
    GET(ROUTE, {}, true)
      .then(({ data: res }) => setData(res))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggle = (type) => setView((prev) => ({ ...prev, [type]: !prev[type] }));

  const closeModal = () => {
    setView({ add: false, edit: false, isDelete: false });
    setEditId(null);
    reset(DEFAULT_FORM);
  };

  const onEditClick = (item) => {
    setEditId(item.id);
    reset({
      maxWeight: item.maxWeight ?? "",
      bonus: Object.fromEntries(BONUS_KEYS.map((k) => [k, item.bonus?.[k] ?? ""])),
    });
    toggle("edit");
  };

  const onSubmit = (formData) => {
    const payload = {
      maxWeight: formData.maxWeight !== "" && formData.maxWeight !== null ? Number(formData.maxWeight) : null,
      bonus: Object.fromEntries(BONUS_KEYS.map((k) => [k, Number(formData.bonus[k]) || 0])),
    };

    setLoading(true);
    const request = editId
      ? PUT(`${ROUTE}/${editId}`, payload, {}, true)
      : POST(ROUTE, payload, {}, true);

    request
      .then(() => {
        toast.success(editId ? "Cập nhật thành công" : "Thêm mới thành công");
        closeModal();
        fetchData();
      })
      .catch(({ response }) => {
        toast.error(response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại.");
      })
      .finally(() => setLoading(false));
  };

  const onDeleteConfirm = () => {
    if (!deleteId) return;
    DELETE(`${ROUTE}/${deleteId}`, {}, true)
      .then(() => {
        toast.success("Xóa thành công");
        closeModal();
        fetchData();
      })
      .catch(({ response }) => {
        toast.error(response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại.");
      });
  };

  const columns = [
    {
      Header: "Khoảng sản lượng (kg)",
      accessor: (item, rowIndex) => {
        const prevMax = rowIndex > 0 ? data[rowIndex - 1].maxWeight : 0;
        const fromLabel = prevMax != null
          ? <NumericFormat displayType="text" value={prevMax} thousandSeparator="," decimalScale={0} />
          : "0";
        const toLabel = item.maxWeight != null
          ? <NumericFormat displayType="text" value={item.maxWeight} thousandSeparator="," decimalScale={0} />
          : <span className="text-muted">Không giới hạn</span>;
        return <div className="text-right">{fromLabel} → {toLabel}</div>;
      },
    },
    ...BONUS_KEYS.map((key) => ({
      Header: `Hoa hồng ${INGREDIENT_CATEGORY_NAME[key] || key} (đ)`,
      accessor: (item) => (
        <div className="text-right">
          <NumericFormat displayType="text" value={item.bonus?.[key]} thousandSeparator="," decimalScale={0} />
        </div>
      ),
    })),
    {
      Header: "Hành động",
      accessor: (item) => (
        <ul className="nk-tb-actions gx-1">
          <li>
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="btn btn-icon dropdown-toggle btn-trigger">
                <Icon name="more-h" />
              </DropdownToggle>
              <DropdownMenu right>
                <ul className="link-list-opt no-bdr">
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#edit"
                      onClick={(e) => { e.preventDefault(); onEditClick(item); }}
                    >
                      <Icon name="edit" />
                      <span>Cập nhật</span>
                    </DropdownItem>
                  </li>
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#delete"
                      onClick={(e) => {
                        e.preventDefault();
                        setDeleteId(item.id);
                        toggle("isDelete");
                      }}
                    >
                      <Icon name="trash" className="text-danger" />
                      <span>Xóa</span>
                    </DropdownItem>
                  </li>
                </ul>
              </DropdownMenu>
            </UncontrolledDropdown>
          </li>
        </ul>
      ),
    },
  ];

  const renderFormFields = () => (
    <>
      <div className="form-group">
        <label className="form-label">
          Mức tối đa (kg) <span className="text-muted">(để trống = không giới hạn)</span>
        </label>
        <Controller
          name="maxWeight"
          control={control}
          render={({ onChange, value }) => (
            <NumericFormat
              className="form-control"
              placeholder="Ví dụ: 300,000"
              thousandSeparator=","
              decimalScale={0}
              value={value}
              onValueChange={(values) => onChange(values.floatValue ?? "")}
            />
          )}
        />
        <ErrorMessage errors={errors} name="maxWeight" render={({ message }) => (
          <span className="text-danger fs-11px">{message}</span>
        )} />
      </div>
      {BONUS_KEYS.map((key) => (
        <div className="form-group" key={key}>
          <label className="form-label">
            Hoa hồng {INGREDIENT_CATEGORY_NAME[key] || key} (đ) <span className="text-danger">*</span>
          </label>
          <Controller
            name={`bonus.${key}`}
            control={control}
            rules={{
              required: `Vui lòng nhập hoa hồng ${INGREDIENT_CATEGORY_NAME[key] || key}`,
              min: { value: 0, message: "Giá trị phải >= 0" },
            }}
            render={({ onChange, value }) => (
              <NumericFormat
                className="form-control"
                placeholder="Ví dụ: 1,000,000"
                thousandSeparator=","
                decimalScale={0}
                value={value}
                onValueChange={(values) => onChange(values.floatValue ?? "")}
              />
            )}
          />
          <ErrorMessage errors={errors} name={`bonus.${key}`} render={({ message }) => (
            <span className="text-danger fs-11px">{message}</span>
          )} />
        </div>
      ))}
    </>
  );

  return (
    <React.Fragment>
      <Head title="Cài đặt hoa hồng bán hàng" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Cài đặt hoa hồng bán hàng BHD & DTP</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                onClick={() => { reset(DEFAULT_FORM); toggle("add"); }}
              >
                <Icon name="plus" />
                <span>Thêm mức hoa hồng</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="card card-bordered card-preview">
            <div className="card-inner">
              <Table loading={loading} columns={columns} data={data} notShowPagi />
            </div>
          </div>
        </Block>

        {/* Add Modal */}
        <Modal isOpen={view.add} toggle={() => toggle("add")} size="md">
          <ModalHeader toggle={() => toggle("add")}>Thêm mức hoa hồng bán hàng</ModalHeader>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody>{renderFormFields()}</ModalBody>
            <ModalFooter>
              <Button type="submit" color="primary" disabled={loading}>
                {loading ? "Đang lưu..." : "Lưu"}
              </Button>
              <Button type="button" color="light" onClick={closeModal}>Hủy</Button>
            </ModalFooter>
          </form>
        </Modal>

        {/* Edit Modal */}
        <Modal isOpen={view.edit} toggle={() => toggle("edit")} size="md">
          <ModalHeader toggle={() => toggle("edit")}>Cập nhật mức hoa hồng bán hàng</ModalHeader>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody>{renderFormFields()}</ModalBody>
            <ModalFooter>
              <Button type="submit" color="primary" disabled={loading}>
                {loading ? "Đang lưu..." : "Cập nhật"}
              </Button>
              <Button type="button" color="light" onClick={closeModal}>Hủy</Button>
            </ModalFooter>
          </form>
        </Modal>

        {/* Delete confirmation Modal */}
        <Modal isOpen={view.isDelete} toggle={() => toggle("isDelete")} size="sm">
          <ModalHeader toggle={() => toggle("isDelete")}>Xác nhận xóa</ModalHeader>
          <ModalBody>
            <p>Bạn có chắc chắn muốn xóa mức hoa hồng này không?</p>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" onClick={onDeleteConfirm}>Xóa</Button>
            <Button color="light" onClick={closeModal}>Hủy</Button>
          </ModalFooter>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default SaleCommissionSettingManager;
